<template>
    <div class="modal-full" v-if="release">
        <div class="modal-header">
            <close-icon v-on:click="closeView"></close-icon>
            <h1>Release</h1>
        </div>
        <div class="modal-body no-pad">
            <div class="md:flex md:h-full">
                <div class="release-sidebar md:h-full">
                    <div class="mb-4 text-center">
                        <p class="text-lg font-light">PR #{{ release.pr_id }}</p>
                    </div>

                    <div class="mb-3" v-if="release.pr_requires_auth && release.pr_draft === 0">
                        <label>Approval</label>
                        <p v-if="release.pr_authorised" class="text-green-dark">Approved</p>
                        <p v-if="isFlagged" class="text-red-600">Flagged</p>
                        <p v-if="release.pr_declined && !isFlagged" class="text-red-600">Declined</p>
                        <p v-if="!release.pr_declined && !release.pr_authorised && !isFlagged" class="text-orange">
                            Awaiting Decision</p>
                    </div>

                    <div class="mb-3" v-if="release.pr_authorised">
                        <label>Approved</label>
                        <p>{{ formatDateShort(release.pr_authorised) }}</p>
                    </div>

                    <div class="mb-3">
                        <label>Status</label>
                        <p>{{ release.status }}</p>
                    </div>

                    <div class="mb-3">
                        <label>Business Name</label>
                        <p v-if="release.business">
                            <router-link v-if="allows('users')"
                                         :to="{ name: 'accounts.show', params: { accountId: this.release.business.account_id } }">
                                {{ release.business.name }}
                            </router-link>
                            <span v-else>{{ release.business.name }}</span>
                        </p>
                        <p v-if="!release.business">[Business Removed]</p>
                    </div>

                    <div class="mb-3">
                        <label>Matches</label>
                        <p>{{ release.pr_matches }}</p>
                    </div>

                    <div class="mb-3">
                        <label>Email Opens</label>
                        <p>{{ counts.opens }}</p>
                    </div>

                    <div class="mb-3">
                        <label>Views</label>
                        <p><a href="#" @click.prevent="showAnalyticsList('views')">{{ counts.views }}</a></p>
                    </div>

                    <div class="mb-3">
                        <label>Full Page Views</label>
                        <p><a href="#" @click.prevent="showAnalyticsList('full')">{{ counts.fullpageViews }}</a></p>
                    </div>

                    <div class="mb-3">
                        <label>Copies</label>
                        <p><a href="#" @click.prevent="showAnalyticsList('copies')">{{ counts.copies }}</a></p>
                    </div>

                    <div class="mb-3">
                        <label>Professional Writing Request</label>
                        <p v-if="release.professional_writing_request"><a a href="#"
                                                                          @click.prevent="showProfessionalWritingRequest">View</a>
                        </p>
                        <p v-else>No</p>
                    </div>

                    <div class="mb-3" v-if="checksResults">
                        <label>Checks Results</label>
                        <div class="flex border-t border-gray-300 py-1" v-for="(value, key) in checksResults">
                            <div :class="{'w-3/4': true, 'font-bold': value > 0, 'text-red': value > 0}">
                                {{ convertChecksName(key) }}
                            </div>
                            <div
                                :class="{'w-1/4': true, 'text-right': true, 'font-bold': value > 0, 'text-red': value > 0}">
                                {{ value }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="release-body md:h-full">
                    <form class="profile-form">
                        <div class="form-title">
                            <p class="text-lg font-light">Press Release</p>
                        </div>

                        <div class="alert danger text-center font-bold" v-if="isFlagged">
                            <p><strong>Flagged by {{ release.flags.flagged_by }} on {{ release.flags.flagged }}</strong>
                            </p>
                            <p>Contact {{ release.flags.flagged_by }} before approving release</p>
                        </div>

                        <div class="alert danger text-center font-bold" v-if="release.pr_deleted === 1">
                            <p>Press release has been deleted and is no longer visible</p>
                        </div>

                        <div class="alert danger text-center font-bold" v-if="release.pr_recalled === 1">
                            <p>Press release has been recalled and is no longer visible</p>
                        </div>

                        <div class="mb-3">
                            <label class="block text-gray-800 text-sm mb-2">Headline</label>
                            <input type="text" v-model="release.pr_headline" name="headline"
                                   class="appearance-none border w-full py-2 px-3 text-gray-800 leading-tight focus:outline-none focus:shadow-outline"/>
                        </div>

                        <div class="mb-3">
                            <label class="block text-gray-800 text-sm mb-2">Body</label>
                            <textarea v-model="release.pr_body" name="body"
                                      class="appearance-none border w-full py-2 px-3 text-gray-800 leading-tight focus:outline-none focus:shadow-outline pr-body"/>
                        </div>

                        <div class="mb-3">
                            <label class="block text-gray-800 text-sm mb-2">Notes to Editors</label>
                            <textarea v-model="release.pr_editors_notes" name="editors_notes" rows="6"
                                      class="appearance-none border w-full py-2 px-3 text-gray-800 leading-tight focus:outline-none focus:shadow-outline"/>
                        </div>

                        <div class="mb-3">
                            <label class="block text-gray-800 text-sm mb-2">Summary</label>
                            <input type="text" v-model="release.pr_summary" name="summary"
                                      class="appearance-none border w-full py-2 px-3 text-gray-800 leading-tight focus:outline-none focus:shadow-outline" maxlength="200" />
                            <p class="mt-2 text-sm text-gray-400">This is the preview text for the press room and social media.</p>
                        </div>

                        <div class="form-title section-title">
                            <p class="text-lg font-light">Targeting</p>
                        </div>

                        <div class="mb-3">
                            <label class="block text-gray-800 text-sm mb-2">Sectors</label>

                            <picker-selector
                                v-if="sectors && sectors.length > 0"
                                :max=3
                                placeholder="Select Sectors"
                                v-model="prSectors"
                                input-name="sectors[]"
                                @update:modelValue="addSectors"
                                :options="sectors">
                            </picker-selector>
                        </div>

                        <div class="mb-3">
                            <label class="block text-gray-800 text-sm mb-2">Locations</label>

                            <picker-selector
                                v-if="locations && locations.length > 0"
                                placeholder="Select Locations"
                                v-model="prLocations"
                                input-name="locations[]"
                                @update:modelValue="addLocations"
                                :options="locations">
                            </picker-selector>
                        </div>

                        <template v-if="shouldShowTwitterPostingOption">
                            <div class="form-title section-title">
                                <p class="text-lg font-light">Options</p>
                            </div>

                            <div class="mb-3">
                                <label class="block text-gray-800 text-sm mb-2">Post to @JournoLinkMedia
                                    Twitter?</label>
                                <CheckBox v-model="release.pr_autotweet"/>
                            </div>
                        </template>

                        <div class="form-title section-title">
                            <p class="text-lg font-light">Images</p>
                        </div>

                        <div class="images" v-if="release">
                            <multi-image-drop
                                instance="PressRelease"
                                :instance-id="release.pr_id"
                                :value="release.images"
                            ></multi-image-drop>
                        </div>

                        <div class="mb-4" v-if="release && release.flags.featured_image">
                            <label class="block text-gray-800 text-sm mb-2">Featured Image</label>
                            <div class="md:w-1/3">
                                <div :style="featuredImageStyle"></div>
                            </div>
                        </div>

                        <div class="pr-schedule">
                            <div class="form-title section-title">
                                <p class="text-lg font-light">Schedule</p>
                            </div>

                            <div class="results-list">
                                <div class="results-row">
                                    <div class="results-head" data-width="40%">Schedule</div>
                                    <div class="results-head" data-width="30%">Processed Time</div>
                                    <div class="results-head" data-width="30%">Release Time</div>
                                </div>
                                <template v-if="release.schedules">
                                    <div
                                        class="results-row"
                                        v-for="(schedule, index) in release.schedules"
                                        :key="schedule.id"
                                        @click="handleSelectSchedule(schedule)"
                                    >
                                        <div class="results-cell" data-width="40%">{{ schedule.medialistTitle }}</div>
                                        <div class="results-cell" data-width="30%">
                                            <FormattedTimestamp :value="schedule.processed" :utc="true"
                                                                v-if="schedule.processed"/>
                                        </div>
                                        <div class="results-cell" data-width="30%">
                                            <FormattedTimestamp :value="schedule.release" :utc="true"/>
                                        </div>
                                    </div>
                                </template>
                                <div class="results-row" v-if="!release.schedules.length">
                                    <div class="results-span text-center" data-width="100%">
                                        <p>No schedules defined</p>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </form>
                </div>
            </div>
        </div>

        <div class="modal-footer">
            <div class="md:flex">
                <div class="left md:text-left text-right mb-4 md:mb-0">
                    <div class="actions" v-click-away="hideActions">
                        <div class="actions-menu" v-show="actionsShown">
                            <button class="btn-primary muted" type="button" @click="addSchedule">Add new Schedule</button>
                            <button class="btn-primary muted" type="button" @click="returnToDraft"
                                    v-if="release.status === 'Scheduled'">Return to Draft
                            </button>
                            <button class="btn-danger" type="button" @click="recallRelease"
                                    v-if="release.status === 'Published' && release.pr_recalled !== 1">
                                Recall Press Release
                            </button>
                            <button class="btn-danger" type="button" @click="destroy" v-if="release.pr_deleted !== 1">
                                Delete
                            </button>
                            <button class="btn-primary btn-danger" type="button" @click="undeleteRelease"
                                    v-if="release.pr_deleted === 1">Recover Release
                            </button>
                            <template v-if="shouldShowApprovalButtons">
                                <button class="btn-primary" type="button" @click="handleApproval">Approve</button>
                                <button class="btn-primary" type="button" @click="handleTrust">Trust all Account Releases</button>
                                <button class="btn-danger" type="button" @click="handleDecline">Decline</button>
                                <button class="btn-danger" type="button" @click="handleFlag">Flag</button>
                            </template>
                            <template v-if="shouldShowFlagState">
                                <button class="btn-danger" type="button" @click="handleDecline">Decline</button>
                                <button class="btn-danger" type="button" @click="handleRemoveFlag">Remove Flag</button>
                            </template>
                        </div>
                        <button class="btn-primary muted" type="button" :data-open="actionsShown"
                                @click="actionsShown = !actionsShown">
                            <ChevronUpIcon class="h-6 w-6 inline" />
                            Actions
                        </button>
                    </div>
                </div>

                <div class="middle md:text-middle text-right mb-4 md:mb-0">

                </div>

                <div class="right text-right mb-4 md:mb-0">
                    <button class="btn-tertiary muted" type="button" @click="closeView">Close</button>
                    <button class="btn-primary" type="button" @click="handleReleaseUpdate">Save</button>
                </div>
            </div>
        </div>
        <router-view v-slot="{ Component }">
            <transition name="drop-in" mode="in-out">
                <component :is="Component"></component>
            </transition>
        </router-view>
    </div>
</template>

<script>
import Permissions from '../../../mixins/Permissions'
import FormattedTimestamp from '../../FormattedTimestamp'
import {ChevronUpIcon} from "@heroicons/vue/24/solid"
import {useStore} from "vuex"

export default {
    name: 'ReleasesView',
    mixins: [
        Permissions,
    ],
    components: {
        FormattedTimestamp,
        ChevronUpIcon,
    },
    data() {
        return {
            release: null,
            prSectors: null,
            prLocations: null,
            declineReasons: [],
            counts: {
                opens: 0,
                views: 0,
                fullpageViews: 0,
                copies: 0,
            },
            echoSetup: false,
            actionsShown: false
        }
    },
    computed: {
        checksResults() {
            let checks = this.release.flags['checks_result']

            return checks && JSON.parse(checks)
        },
        /**
         * Determine if the approve and decline buttons should be displayed
         *
         * @return {Boolean}
         */
        shouldShowApprovalButtons() {
            return this.release &&
                this.release.pr_draft === 0 &&
                this.release.pr_requires_auth &&
                !this.release.pr_authorised &&
                !this.release.pr_declined &&
                !this.isFlagged
        },
        /**
         * Determine if the flagged state details should be displayed
         *
         * @return {Boolean}
         */
        shouldShowFlagState() {
            return this.isFlagged
        },
        /**
         * Determine if the release is flagged for attention
         *
         * @return {Boolean}
         */
        isFlagged() {
            return this.release &&
                this.release.flags.flagged !== undefined
        },
        /**
         * Return the style element contents for the featured image display.
         *
         * @return {Object}
         */
        featuredImageStyle() {
            return {
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                paddingTop: '52.4%',
                backgroundImage: 'url(' + this.release.flags.featured_image + ')',
            }
        },

        shouldShowTwitterPostingOption() {
            return !this.release.pr_declined &&
                !this.release.pr_authorised &&
                !this.isFlagged &&
                this.release.schedules.filter(s => s.destination_type === 'JournoLink\\Shared\\Models\\InternalDestination' && s.destination_id === 1).length > 0
        },

        sectors () {
            const store = useStore()
            return store.state.sectors ?? []
        },

        locations () {
            const store = useStore()
            return store.state.locations ?? []
        },
    },
    mounted() {
        this.loadData()
        this.setMetaTitle('View Release | JournoLink Control')
        window.addEventListener('keyup', this.handleKeypress)
    },
    beforeDestroy() {
        window.removeEventListener('keyup', this.handleKeypress)
    },
    beforeRouteUpdate(to, from) {
        if (from.name === 'releases.schedule' || from.name === 'releases.schedule.new') {
            this.loadData()
        }
    },
    methods: {
        loadData() {
            axios.get(route('releases.show', this.$route.params.releaseId))
                .then(response => {
                    this.release = response.data.release
                    this.prSectors = response.data.prSectors
                    this.prLocations = response.data.prLocations

                    // Decline reasons are passed as a keyed object. Convert into a key => value
                    // object to allow SweetAlert to convert into a select input
                    Object.keys(response.data.declineReasons).map((key, index) => {
                        this.declineReasons[key] = response.data.declineReasons[key].title
                    })

                    this.counts.opens = this.release.opens.length
                    this.counts.views = this.release.views.length
                    this.counts.fullpageViews = this.release.fullpage_views.length
                    this.counts.copies = this.release.copies.length

                    this.setMetaTitle('Release Management | JournoLink Control')

                    if (!this.echoSetup) {
                        window.Echo.private('pressrelease.' + this.release.pr_id)
                            .listen('.JournoLink\\Shared\\Events\\PressReleaseApproved', (e) => {
                                this.loadData()
                            })
                            .listen('.JournoLink\\Shared\\Events\\PressReleaseDeclined', (e) => {
                                this.loadData()
                            })
                            .listen('.JournoLink\\Shared\\Events\\PressReleaseUpdated', (e) => {
                                this.loadData()
                            })
                            .listen('.JournoLink\\Shared\\Events\\PressReleaseDeleted', (e) => {
                                this.loadData()
                            })
                        this.echoSetup = true
                    }
                })
                .catch(e => {
                    console.error({e})
                })
        },
        /**
         * Hide the actions box
         */
        hideActions() {
            this.actionsShown = false
        },
        /**
         * Emit the close event to the parent
         *
         * @return {void}
         */
        closeView() {
            this.$root.goBack()
        },
        /**
         * Handle a keypress event
         *
         * @return {void}
         */
        handleKeypress(event) {
            if (event.which == 27) {
                this.closeView()
            }
        },
        /**
         * Update the press release
         *
         * @return {void}
         */
        handleReleaseUpdate() {
            axios.put(route('releases.save', this.release.pr_id), {
                release: this.release,
                sectors: this.prSectors,
                locations: this.prLocations,
            })
                .then(response => {
                    this.release = response.data.release
                    this.prSectors = response.data.prSectors
                    this.prLocations = response.data.prLocations
                    this.$bus.$emit('alert.success', 'Release has been updated')
                })
                .catch((e) => {
                    console.error(e)
                })
        },
        /**
         * Show the press release schedule
         *
         * @return {void}
         */
        handleSelectSchedule(schedule) {
            this.$router.push({name: 'releases.schedule', params: {releaseId: this.release.pr_id, schedule: schedule.id}})
        },
        addLocations(ids) {
            this.prLocations = ids
        },
        addSectors(ids) {
            this.prSectors = ids
        },
        /**
         * Show the press release schedule
         *
         * @return {void}
         */
        addSchedule() {
            this.$router.push({
                name: 'releases.schedule.new',
                params: {
                    releaseId: this.release.pr_id,
                }
            })
        },
        /**
         * Return the press release to a draft state.
         *
         * @return {void}
         */
        returnToDraft() {
            this.alert.fire({
                title: 'Return to Draft?',
                text: 'Are you sure you want to return this press release to a draft?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                cancelButtonText: 'No, go back'
            })
                .then(selected => {
                    if (selected.value) {
                        axios.put(route('releases.save', this.release.pr_id), {
                            release: {
                                pr_draft: 1,
                            },
                        })
                            .then(response => {
                                this.$bus.$emit('alert.success', 'Press Release has been updated')
                                this.release.pr_draft = 1
                                this.release.status = 'Draft'
                            })
                            .catch(e => {
                                console.error(e)
                            })
                    }
                })
        },
        recallRelease() {
            this.alert.fire({
                title: 'Confirm Release Recall',
                text: 'This is irreversible, and will remove the PR from all listings. Are you sure?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#e3342f',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.delete(route('releases.recall', this.release.pr_id))
                            .then(response => {
                                this.$bus.$emit('alert.success', 'Press Release has been recalled')
                                this.release.pr_recalled = 1
                                this.release.status = 'Recalled'
                            })
                            .catch(e => {
                                console.error(e)
                            })
                    }
                })
        },
        destroy() {
            this.alert.fire({
                title: 'Confirm Delete',
                text: 'Are you sure you want to delete this record?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#e3342f',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.delete(route('releases.delete', this.release.pr_id))
                            .then(response => {
                                this.$bus.$emit('alert.success', 'Press Release has been deleted')
                                this.release.pr_deleted = 1
                                this.release.status = 'Deleted'
                            })
                            .catch(e => {
                                console.error(e)
                            })
                    }
                })
        },
        undeleteRelease() {
            this.alert.fire({
                title: 'Confirm Recovery',
                text: 'This will reverse the deletion of this press release.\nAre you sure you want to do this?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#e3342f',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.post(route('releases.undelete', this.release.pr_id))
                            .then(response => {
                                this.$bus.$emit('alert.success', 'Press Release has been recovered')
                                this.release.pr_deleted = 0
                                this.release.status = 'Draft'
                            })
                            .catch(e => {
                                console.error(e)
                            })
                    }
                })
        },
        /**
         * Approve the press release
         *
         * @return {void}
         */
        handleApproval() {
            this.alert.fire({
                title: 'Are you sure?',
                text: 'Approving the Press Release will allow it to be publicly released',
                icon: 'warning',
                showCancelButton: true,
                cancelButtonText: 'Go Back',
                confirmButtonText: 'Approve it',
                reverseButtons: true,
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.post(route('releases.approve', this.release.pr_id))
                            .then(response => {
                                this.release = response.data.release
                                this.$bus.$emit('alert.success', 'Release has been approved for release')
                            })
                            .catch((e) => {
                                console.error(e)
                            })
                    }

                })

        },
        handleTrust() {
            this.alert.fire({
                title: 'Are you sure?',
                text: 'If you trust all press releases by this account, they will no longer be flagged for review. You can amend these settings within the user account.',
                icon: 'warning',
                showCancelButton: true,
                cancelButtonText: 'Go Back',
                confirmButtonText: 'Trust them',
                reverseButtons: true,
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.post(route('releases.trust', this.release.pr_id))
                            .then(response => {
                                this.release = response.data.release
                                this.$bus.$emit('alert.success', 'Release has been approved for release')
                            })
                            .catch((e) => {
                                console.error(e)
                            })
                    }

                })

        },
        /**
         * Decline the press release
         *
         * @return {void}
         */
        handleDecline() {
            this.alert.fire({
                title: 'Are you sure?',
                text: 'Declining the Press Release will prevent it from being publicly released',
                icon: 'warning',
                showCancelButton: true,
                cancelButtonText: 'Go Back',
                confirmButtonText: 'Decline it',
                confirmButtonColor: '#E3342F',
                reverseButtons: true,
                input: 'select',
                inputOptions: this.declineReasons,
            })
                .then((selected) => {
                    if (selected.value) {
                        switch (selected.value) {
                            case 'other':
                                this.alert.fire({
                                    title: 'Enter reason',
                                    text: 'Enter the reason that this press release was declined. This will be sent to the client for review.',
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonColor: '#E3342F',
                                    reverseButtons: true,
                                    input: 'text',
                                })
                                    .then(input => {
                                        if (input.value) {
                                            this.sendDeclineToServer(input.value)
                                        }
                                    })
                                break
                            default:
                                if (selected.value) {
                                    this.sendDeclineToServer(selected.value)
                                }
                                break
                        }
                    }
                })
        },
        /**
         * Send the decline data to the server
         *
         * @param  {String} reasonText
         * @return {void}
         */
        sendDeclineToServer(reasonText) {
            axios.post(route('releases.decline', this.release.pr_id), {
                'reason': reasonText
            })
                .then(response => {
                    this.release = response.data.release
                    this.$bus.$emit('alert.success', 'Press release has been declined')
                })
                .catch((e) => {
                    console.error(e)
                    this.$bus.$emit('alert.error', 'Failed to communicate with the server. Please do not try again. Contact Tech.')
                })
        },
        /**
         * Flag a press release for attention
         *
         * @return {void}
         */
        handleFlag() {
            this.alert.fire({
                title: 'Are you sure?',
                text: 'Flagging this press release will prevent it from being approved until it has been reviewed. The client will not be informed of this.',
                icon: 'warning',
                showCancelButton: true,
                cancelButtonText: 'Go Back',
                confirmButtonText: 'Continue',
                confirmButtonColor: '#E3342F',
                reverseButtons: true,
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.post(route('releases.flag', this.release.pr_id), {state: 1})
                            .then(response => {
                                this.release = response.data.release
                                this.$bus.$emit('alert.success', 'Press release has been flagged for attention')
                            })
                            .catch((e) => {
                                console.error(e)
                                this.$bus.$emit('alert.error', 'Failed to communicate with the server. Please do not try again. Contact Tech.')
                            })
                    }
                })
        },
        /**
         * Unflag a press release for attention
         *
         * @return {void}
         */
        handleRemoveFlag() {
            this.alert.fire({
                title: 'Are you sure?',
                text: 'Removing the flag from this release will allow it be to approved for release. Please ensure you have confirmed with ' + this.release.flags.flagged_by + ' before continuing',
                icon: 'warning',
                showCancelButton: true,
                cancelButtonText: 'Go Back',
                confirmButtonText: 'Continue',
                confirmButtonColor: '#E3342F',
                reverseButtons: true,
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.post(route('releases.flag', this.release.pr_id), {state: 0})
                            .then(response => {
                                this.release = response.data.release
                                this.$bus.$emit('alert.success', 'Press release flag has been removed')
                            })
                            .catch((e) => {
                                console.error(e)
                                this.$bus.$emit('alert.error', 'Failed to communicate with the server. Please do not try again. Contact Tech.')
                            })
                    }
                })
        },
        /**
         * Render modal view
         *
         * @param  {String} type
         * @return {void}
         */
        showAnalyticsList(type) {
            this.$router.push({
                name: 'releases.analytics',
                params: {
                    type: type,
                }
            })
        },
        /**
         * Convert the name of a Check to human readable format
         *
         * @param name
         * @returns {string}
         */
        convertChecksName(name) {
            let parts = name.split('_')

            return parts.map(part => {
                return part.substring(0, 1).toUpperCase() + part.substring(1).toLowerCase()
            }).join(' ')
        },
        /**
         * Show Professional Writing Request
         *
         */
        showProfessionalWritingRequest() {
            this.$router.push({
                name: 'professionalWriting.show',
                params: {
                    professionalWritingId: this.release.professional_writing_request.id,
                }
            })
        },
    },
}
</script>

<style lang="less">
.release-sidebar {
    padding: 15px;
    width: 250px;
    font-size: 0.9rem;
    background: #f6f6f6;

    @media (max-width: 767px) {
        width: 100%;
    }

    label {
        font-weight: bold;
        font-size: 0.8em;
        color: #999999;
    }

    p {
        margin-bottom: 0.5em;
    }
}

.release-body {
    flex: 1;
    max-width: calc(100% - 250px);
    padding: 15px;

    @media (max-width: 767px) {
        flex: 0;
        width: 100%;
        max-width: 100%;
        padding-bottom: 200px;
    }
}

textarea.pr-body {
    height: 350px;
}

.images {
    display: flex;

    .pr-image {
        display: block;
        width: 24%;
        margin-right: 15px;
        padding: 10px;
        border: 1px solid #ddd;

        .thumbnail {
            position: relative;
            width: 150px;
            height: 150px;
            overflow: hidden;

            img {
                position: absolute;
                left: 50%;
                top: 50%;
                height: 100%;
                width: auto;
                -webkit-transform: translate(-50%, -50%);
            }
        }
    }
}

.actions-menu {
    position: fixed;
    left: 0;
    bottom: 72px;
    padding: 15px;
    background: #dde0e2;

    > button {
        margin-bottom: 10px;
        display: block;
        width: 100%;
    }
}

</style>
